import { forwardRef, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cva, type VariantProps } from 'class-variance-authority';
import mergeRefs from 'merge-refs';
import { cn } from 'v5/platform/dom/cn';
import { focusRings } from './_common/focusRing';

const ternaryToggleVariants = cva(
  'relative inline-flex w-fit items-center gap-1 rounded border border-border-default bg-bg-subtle isolate disabled:cursor-not-allowed aria-[readonly=true]:cursor-default disabled:opacity-50 aria-[disabled=true]:opacity-50',
  {
    variants: {
      size: {
        xs: 'h-7 p-0.5',
        sm: 'h-9 p-0.5',
        md: 'h-12 p-1',
        lg: 'h-15 p-1',
        xl: 'h-18 p-1.5'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

const labelVariants = cva(
  'relative z-10 flex items-center self-stretch rounded-[3px] font-medium cursor-pointer aria-[disabled=true]:cursor-not-allowed aria-[readonly=true]:cursor-default',
  {
    variants: {
      size: {
        xs: 'px-2 text-xs',
        sm: 'px-3 text-base',
        md: 'px-4 text-base',
        lg: 'px-4 text-lg',
        xl: 'px-6 text-xl'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

const dotContainerVariants = cva(
  'group relative z-10 flex items-center justify-center rounded-[3px] cursor-pointer aria-[disabled=true]:cursor-not-allowed aria-[readonly=true]:cursor-default',
  {
    variants: {
      size: {
        xs: 'h-5 w-5',
        sm: 'h-7 w-7',
        md: 'h-9 w-9',
        lg: 'h-12 w-12',
        xl: 'h-14 w-14'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

const dotVariants = cva('rounded-full', {
  variants: {
    size: {
      xs: 'h-0.75 w-0.75',
      sm: 'h-1 w-1',
      md: 'h-1.5 w-1.5',
      lg: 'h-2 w-2',
      xl: 'h-2.5 w-2.5'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

type TernaryToggleProps = Omit<
  React.ComponentPropsWithoutRef<'fieldset'>,
  'onChange'
> &
  VariantProps<typeof ternaryToggleVariants> & {
    value: boolean | null;
    onChange: (value: boolean | null) => void;
    visuallyDisabled?: boolean;
    readOnly?: boolean;
    onChangeEvenWhenDisabled?: (value: boolean | null) => void;
  };

export const TernaryToggle = forwardRef<
  HTMLFieldSetElement,
  TernaryToggleProps
>(
  (
    {
      value,
      onChange,
      className,
      name,
      size,
      disabled,
      visuallyDisabled,
      readOnly,
      onChangeEvenWhenDisabled,
      ...fieldsetProps
    },
    ref
  ) => {
    const { t } = useTranslation('ds');
    const groupId = useId();
    const generatedName = name || `ternary-toggle-${groupId}`;
    const containerRef = useRef<HTMLFieldSetElement>(null);
    const noLabelRef = useRef<HTMLLabelElement>(null);
    const nullLabelRef = useRef<HTMLLabelElement>(null);
    const yesLabelRef = useRef<HTMLLabelElement>(null);
    const [sliderStyle, setSliderStyle] = useState<React.CSSProperties | null>(
      null
    );

    const isDisabled = disabled || visuallyDisabled;
    const isInteractive = !disabled && !visuallyDisabled && !readOnly;

    const handleChange = (newValue: boolean | null) => {
      if (isInteractive) {
        onChange(newValue);
      }
      onChangeEvenWhenDisabled?.(newValue);
    };

    const updateSliderPosition = () => {
      const activeLabel =
        value === false
          ? noLabelRef.current
          : value === true
            ? yesLabelRef.current
            : nullLabelRef.current;

      if (activeLabel) {
        setSliderStyle({
          width: `${activeLabel.offsetWidth}px`,
          height: `${activeLabel.offsetHeight}px`,
          left: `${activeLabel.offsetLeft}px`
        });
      } else {
        setSliderStyle(null);
      }
    };

    useLayoutEffect(() => {
      updateSliderPosition();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    return (
      <fieldset
        ref={mergeRefs(containerRef, ref)}
        className={cn(
          ternaryToggleVariants({ size }),
          focusRings.focusWithin.visible,
          className
        )}
        aria-disabled={isDisabled}
        aria-readonly={readOnly ? 'true' : undefined}
        disabled={disabled}
        {...fieldsetProps}
      >
        {/* Animated background slider */}
        {sliderStyle && (
          <div
            className={cn(
              'absolute rounded-[3px] bg-bg-default shadow-sm transition-all duration-200',
              value === true && 'bg-bg-success-emphasis-2'
            )}
            style={sliderStyle}
            aria-hidden="true"
          />
        )}

        <input
          type="radio"
          name={generatedName}
          value="false"
          checked={value === false}
          onChange={() => handleChange(false)}
          className="sr-only peer/no"
          id={`${generatedName}-no`}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          aria-disabled={isDisabled}
        />
        <label
          ref={noLabelRef}
          htmlFor={`${generatedName}-no`}
          aria-disabled={isDisabled}
          aria-readonly={readOnly ? 'true' : undefined}
          className={cn(
            labelVariants({ size }),
            value === false
              ? 'text-fg-default'
              : cn(
                  'text-fg-muted',
                  isInteractive &&
                    'hover:text-fg-default hover:bg-bg-default/50'
                )
          )}
        >
          {t('TernaryToggle.no')}
        </label>

        <input
          type="radio"
          name={generatedName}
          value="null"
          checked={value === null}
          onChange={() => handleChange(null)}
          className="sr-only peer/null"
          id={`${generatedName}-null`}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          aria-disabled={isDisabled}
        />
        <label
          ref={nullLabelRef}
          htmlFor={`${generatedName}-null`}
          aria-disabled={isDisabled}
          aria-readonly={readOnly ? 'true' : undefined}
          className={cn(
            dotContainerVariants({ size }),
            value !== null && isInteractive && 'hover:bg-bg-default/50'
          )}
          aria-label={t('TernaryToggle.notSet')}
        >
          <div
            className={cn(
              dotVariants({ size }),
              value === null
                ? 'bg-fg-default'
                : cn(
                    'bg-fg-muted',
                    isInteractive && 'group-hover:bg-fg-default'
                  )
            )}
          />
        </label>

        <input
          type="radio"
          name={generatedName}
          value="true"
          checked={value === true}
          onChange={() => handleChange(true)}
          className="sr-only peer/yes"
          id={`${generatedName}-yes`}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          aria-disabled={isDisabled}
        />
        <label
          ref={yesLabelRef}
          htmlFor={`${generatedName}-yes`}
          aria-disabled={isDisabled}
          aria-readonly={readOnly ? 'true' : undefined}
          className={cn(
            'transition-colors',
            labelVariants({ size }),
            value === true
              ? cn('text-fg-surface', isInteractive && 'hover:bg-bg-default/20')
              : cn(
                  'text-fg-muted',
                  isInteractive &&
                    'hover:text-fg-default hover:bg-bg-default/50'
                )
          )}
        >
          {t('TernaryToggle.yes')}
        </label>
      </fieldset>
    );
  }
);

TernaryToggle.displayName = 'TernaryToggle';
